/**
 * Las marcas y etiquetas del proyecto, compartidas por todas las pantallas: el
 * asistente de ofertas, la ficha y las condiciones de las colecciones leen la
 * misma lista, así que lo que se crea o se elimina en una aparece en las demás.
 *
 * Una petición por proyecto mientras nadie avise de cambios
 * (`markOfferLabelsStale`); al avisar, se vuelve a pedir y se repinta todo.
 */
import { useEffect, useState } from 'react'
import { listOfferLabels, type OfferLabels } from './api'
import { onOfferLabelsStale } from './offerLabelsEvents'

const EMPTY: OfferLabels = { brands: [], tags: [] }
const cache = new Map<string, OfferLabels>()
const pending = new Map<string, Promise<OfferLabels>>()
const subscribers = new Set<(projectId: string) => void>()

function load(projectId: string, token: string): Promise<OfferLabels> {
  const running = pending.get(projectId)
  if (running) return running
  const request = listOfferLabels(projectId, token)
    .then((labels) => { cache.set(projectId, labels); subscribers.forEach((notify) => notify(projectId)); return labels })
    .finally(() => { pending.delete(projectId) })
  pending.set(projectId, request)
  return request
}

let lastToken = ''
onOfferLabelsStale((projectId) => {
  cache.delete(projectId)
  if (lastToken) void load(projectId, lastToken).catch(() => undefined)
})

export function useOfferLabels(projectId: string, token: string): OfferLabels {
  const [labels, setLabels] = useState<OfferLabels>(() => cache.get(projectId) ?? EMPTY)
  useEffect(() => {
    lastToken = token
    const notify = (changed: string) => { if (changed === projectId) setLabels(cache.get(projectId) ?? EMPTY) }
    subscribers.add(notify)
    const cached = cache.get(projectId)
    if (cached) setLabels(cached)
    else void load(projectId, token).catch(() => undefined)
    return () => { subscribers.delete(notify) }
  }, [projectId, token])
  return labels
}
